/**
 * Zeilendarstellung aus dem Readmodel: durchgehende Buchungsbalken über dem Raster und je Tag
 * genau ein fachlicher Zustand – beides hier, damit Zelle und Balken nie auseinanderlaufen.
 */
import type {
  AvailabilityDistributionApartment,
  AvailabilityDistributionBooking,
  AvailabilityDistributionChannelCloseout,
  AvailabilityDistributionDay,
  EarlyDeparturePlanSummary,
} from '@/shared/api';
import { addIsoDays, diffIsoDays } from './calendarDates';

export type BookingSource = AvailabilityDistributionBooking['source'];
export type CellKind =
  | 'booked'
  | 'doubleBooked'
  | 'closedByRule'
  | 'blocked'
  | 'closedByFamily'
  | 'open';

export interface BookingBar {
  key: string;
  source: BookingSource;
  startIndex: number;
  nights: number;
  start: string;
  endExclusive: string;
  clippedStart: boolean;
  clippedEnd: boolean;
  /** Ein Aufenthalt endet am selben Tag, an dem dieser beginnt — Gegen-Diagonale statt Kante. */
  hasTransitionIn: boolean;
  /** Ein Aufenthalt beginnt am Abreisetag dieses Balkens. */
  hasTransitionOut: boolean;
  earlyDeparturePlan: EarlyDeparturePlanSummary | null;
  /** Bahn 0 oder 1: überschneiden sich zwei Aufenthalte, liegt der spätere darunter. */
  lane: number;
}

export interface DayCellModel {
  index: number;
  day: AvailabilityDistributionDay;
  kind: CellKind;
  closeout: AvailabilityDistributionChannelCloseout | null;
}

export interface ApartmentModel {
  dates: string[];
  bars: BookingBar[];
  cells: DayCellModel[];
  closeouts: AvailabilityDistributionChannelCloseout[];
}

export function buildApartmentModel(
  apartment: AvailabilityDistributionApartment,
  allCloseouts: readonly AvailabilityDistributionChannelCloseout[],
): ApartmentModel {
  const dates = apartment.days.map((day) => day.date);
  const closeouts = allCloseouts.filter((rule) => rule.propertyIds.includes(apartment.propertyId));
  const bars = buildBookingBars(apartment.bookings, dates);
  const bookedDays = new Set(bars.flatMap((bar) => coveredDates(bar)));
  const cells = apartment.days.map((day, index) => {
    const closeout = closeoutAt(closeouts, day.date);
    return { index, day, closeout, kind: cellKind(day, bookedDays.has(day.date), closeout) };
  });
  return { dates, bars, cells, closeouts };
}

export function buildBookingBars(
  bookings: readonly AvailabilityDistributionBooking[],
  dates: readonly string[],
): BookingBar[] {
  const first = dates[0];
  const last = dates[dates.length - 1];
  if (!first || !last) return [];
  const windowEndExclusive = addIsoDays(last, 1);
  const departures = new Set(bookings.map((booking) => booking.endExclusive));
  const arrivals = new Set(bookings.map((booking) => booking.start));
  return bookings
    .map((booking, position) => {
      const start = booking.start < first ? first : booking.start;
      const endExclusive =
        booking.endExclusive > windowEndExclusive ? windowEndExclusive : booking.endExclusive;
      const nights = diffIsoDays(start, endExclusive);
      if (nights <= 0) return null;
      const clippedStart = booking.start < first;
      const clippedEnd = booking.endExclusive > windowEndExclusive;
      return {
        key: `${booking.source}-${booking.start}-${position}`,
        source: booking.source,
        startIndex: diffIsoDays(first, start),
        nights,
        start,
        endExclusive,
        clippedStart,
        clippedEnd,
        hasTransitionIn: !clippedStart && departures.has(booking.start),
        // Der Abreisetag braucht eine eigene Spalte, sonst liefe die Diagonale aus dem Raster.
        hasTransitionOut: !clippedEnd && endExclusive <= last && arrivals.has(booking.endExclusive),
        earlyDeparturePlan: booking.earlyDeparturePlan ?? null,
        lane: 0,
      } satisfies BookingBar;
    })
    .filter((bar): bar is BookingBar => bar !== null)
    .sort((left, right) => left.startIndex - right.startIndex)
    .map(assignLane());
}

/** Bahn 0, sobald sie wieder frei ist; sonst Bahn 1 – ein dritter Aufenthalt teilt sich Bahn 1. */
function assignLane() {
  let laneZeroEnd = -1;
  return (bar: BookingBar): BookingBar => {
    if (bar.startIndex < laneZeroEnd) return { ...bar, lane: 1 };
    laneZeroEnd = bar.startIndex + bar.nights;
    return { ...bar, lane: 0 };
  };
}

export function closeoutAt(
  closeouts: readonly AvailabilityDistributionChannelCloseout[],
  date: string,
): AvailabilityDistributionChannelCloseout | null {
  return closeouts.find((rule) => rule.startDate <= date && rule.endDate >= date) ?? null;
}

/**
 * Eine Buchung deckt den Tag und ist damit keine bearbeitbare Sperre — belegt der Verbund ihn
 * zugleich, ist er doppelt belegt; danach zählt die eigene Sperre, dann der Wohnungsverbund und
 * zuletzt die Kanalregel. Die Regel schließt nur einen Kanal: Preis und Mindestnächte bleiben
 * sichtbar, ihre Marke steht an jeder Zelle, die sie trifft (`closeout`).
 */
function cellKind(
  day: AvailabilityDistributionDay,
  booked: boolean,
  closeout: AvailabilityDistributionChannelCloseout | null,
): CellKind {
  if (booked) return day.closedByFamily ? 'doubleBooked' : 'booked';
  if (day.manualBlock || day.stopSell || !day.available) return 'blocked';
  if (day.closedByFamily) return 'closedByFamily';
  if (closeout) return 'closedByRule';
  return 'open';
}

function coveredDates(bar: BookingBar): string[] {
  return Array.from({ length: bar.nights }, (_, offset) => addIsoDays(bar.start, offset));
}
